"use client"

import { useState, useEffect } from "react"
import { Link } from 'react-router-dom'
import slide1 from '../images/slide1.jpg';
import slide2 from '../images/slide2.jpg';
import slide3 from '../images/slide3.jpg';
import slide4 from '../images/slide4.jpg';
import slide5 from '../images/slide5.jpg';
import slide6 from '../images/slide6.webp';

export default function PSDCTLiveMode() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  const slides = [
    slide1,
    slide2,
    slide3,
    slide4,
    slide5,
    slide6
  ]

  // Slideshow auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 4000)

    return () => clearInterval(interval)
  }, [slides.length])

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Content animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible")
        }
      })
    }, observerOptions)

    const contentElements = document.querySelectorAll(".content-animate")
    contentElements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <style jsx>{`
        /* Gradient animation for logo */
        @keyframes gradient-x {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
        }

        /* Same animations and effects from the about page */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* PSDCT section with animated background */
        .psdct-section {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        /* Animated background elements */
        .bg-element-1 {
          position: absolute;
          top: 80px;
          left: 40px;
          width: 288px;
          height: 288px;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.2), rgba(139, 92, 246, 0.1));
          border-radius: 50%;
          filter: blur(48px);
          animation: pulse 4s infinite;
        }

        .bg-element-2 {
          position: absolute;
          bottom: 80px;
          right: 40px;
          width: 384px;
          height: 384px;
          background: linear-gradient(270deg, rgba(124, 58, 237, 0.2), rgba(6, 182, 212, 0.1));
          border-radius: 50%;
          filter: blur(48px);
          animation: pulse 4s infinite;
          animation-delay: 1s;
        }

        .bg-element-3 {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 800px;
          height: 800px;
          background: radial-gradient(circle, transparent 0%, rgba(41, 1, 1, 0.05) 50%, transparent 100%);
          border-radius: 50%;
          animation: spin-slow 20s linear infinite;
          pointer-events: none;
        }

        /* Title animations */
        .title-gradient {
          background: linear-gradient(120deg, #0f766e 0%, #7c3aed 50%, #0f766e 100%);
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradient-x 4s ease infinite;
          position: relative;
        }

        .title-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.2), rgba(59, 130, 246, 0.2));
          filter: blur(24px);
          animation: pulse 3s infinite;
        }

        /* Content animations */
        .content-animate {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .content-animate.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Card hover effects */
        .feature-card {
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .feature-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.1), transparent);
          transition: left 0.5s ease;
        }

        .feature-card:hover::before {
          left: 100%;
        }

        /* Enhanced button styling */
        .cta-button {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #290101 0%, #3d0303 100%);
          transition: all 0.3s ease;
        }

        .cta-button:hover {
          transform: scale(1.05) translateY(-2px);
          box-shadow: 0 15px 30px rgba(41, 1, 1, 0.4);
        }

        .cta-button::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .cta-button:hover::before {
          left: 100%;
        }

        /* Footer animations */
        .footer-link {
          padding: 8px 16px;
          font-size: 14px;
          color: #d1d5db;
          transition: all 0.3s ease;
          border: 1px solid #4b5563;
          border-radius: 8px;
          position: relative;
          overflow: hidden;
          transform: translateY(0);
          text-decoration: none;
        }

        .footer-link:hover {
          color: white;
          background: linear-gradient(90deg, #06b6d4, #7c3aed);
          border-color: #06b6d4;
          box-shadow: 0 0 15px rgba(6, 182, 212, 0.5);
          transform: scale(1.05) translateY(-4px);
        }

        .footer-link::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.2), rgba(124, 58, 237, 0.2));
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: -1;
        }

        .footer-link:hover::before {
          opacity: 1;
        }

        .footer-link::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: #06b6d4;
          transition: width 0.3s ease;
        }

        .footer-link:hover::after {
          width: 100%;
        }

        .footer-link span {
          position: relative;
          z-index: 10;
        }

        /* Fade in animations */
        .fade-in-element {
          opacity: 0;
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .fade-in-delay-1 {
          animation-delay: 0.2s;
        }

        .fade-in-delay-2 {
          animation-delay: 0.4s;
        }

        .fade-in-delay-3 {
          animation-delay: 0.6s;
        }

        .fade-in-delay-4 {
          animation-delay: 0.8s;
        }

        /* Slideshow background styles */
        .slideshow-container {
          position: absolute;
          inset: 0;
        }

        .slide {
          position: absolute;
          inset: 0;
          transition: opacity 1s ease-in-out;
          opacity: 0;
        }

        .slide.active {
          opacity: 0.3;
        }

        .slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Overlay gradients */
        .slideshow-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(15, 118, 110, 0.2) 0%, transparent 50%, rgba(124, 58, 237, 0.2) 100%);
        }

        .slideshow-overlay-2 {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(15, 118, 110, 0.3) 0%, transparent 50%, rgba(15, 118, 110, 0.1) 100%);
        }

        /* Enhanced CTA button with new colors and animations */
        .cta-button {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #ea580c 100%);
          background-size: 200% 100%;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          box-shadow: 0 8px 32px rgba(245, 158, 11, 0.3);
        }

        .cta-button:hover {
          background-position: 100% 0;
          transform: scale(1.08) translateY(-4px);
          box-shadow: 0 20px 40px rgba(245, 158, 11, 0.5);
          border-color: #fbbf24;
        }

        .cta-button::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
          transition: left 0.6s ease;
        }

        .cta-button:hover::before {
          left: 100%;
        }

        .cta-button::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .cta-button:hover::after {
          opacity: 1;
        }

        /* Pulsing glow effect */
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 8px 32px rgba(245, 158, 11, 0.3);
          }
          50% {
            box-shadow: 0 8px 32px rgba(245, 158, 11, 0.6), 0 0 60px rgba(245, 158, 11, 0.2);
          }
        }

        .cta-button {
          animation: pulse-glow 3s ease-in-out infinite;
        }

        .cta-button:hover {
          animation: none;
        }
      `}</style>

      <div className="bg-white text-gray-800">
        {/* Navbar - Same as About Page */}
        <header
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
            isScrolled
              ? "bg-white/20 backdrop-blur-2xl border-b border-white/10 shadow-xl"
              : "bg-gray-900/30 backdrop-blur-xl text-white border-b border-white/5"
          }`}
        >
          <div className="container mx-auto flex justify-between items-center px-6 py-4">
            <div
              className={`text-2xl font-bold transition-all duration-500 ${isScrolled ? "text-[#06b6d4]" : "text-white"}`}
            >
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#00d4ff] via-[#5b21b6] to-[#ec4899] bg-clip-text text-transparent animate-gradient-x bg-[length:200%_100%]">
                  StructuralGuru
                </span>
                <div className="absolute -inset-3 bg-gradient-to-r from-[#00d4ff]/20 via-[#5b21b6]/20 to-[#ec4899]/20 rounded-2xl blur-2xl opacity-50 animate-pulse"></div>
                <div
                  className="absolute -inset-4 bg-gradient-to-r from-[#00d4ff]/15 via-[#5b21b6]/15 to-[#ec4899]/15 rounded-3xl blur-3xl opacity-30 animate-pulse"
                  style={{ animationDelay: "1s" }}
                ></div>
              </span>
            </div>
            <nav className="hidden md:flex space-x-8">
              {[
                { name: "HOME", to: "/" },
                { name: "ABOUT US", to: "/about" },
                { name: "SERVICES", to: "/services" },
                { name: "OUR ENGG PROJECTS", to: "/projects" },
              ].map(({ name, to }) => (
                <Link
                  key={name}
                  to={to}
                  className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                    isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                  } ${
                    isScrolled
                      ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                      : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                  } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
                >
                  {name}
                </Link>
              ))}

              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center space-x-1 font-medium transition-all duration-300 hover:scale-105 ${
                    isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                  } ${
                    isScrolled
                      ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                      : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                  } rounded-lg px-2 py-1`}
                >
                  <span>MORE</span>
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                <div
                  className={`absolute top-full right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/20 overflow-hidden transition-all duration-300 ${
                    isDropdownOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible"
                  }`}
                >
                  <Link
                    to="/privacy"
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/quality"
                    className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  >
                    Quality Policy
                  </Link>
                </div>
              </div>
            </nav>

            {/* Mobile menu button */}
            <button className="md:hidden">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </header>

        {/* PSDCT Section with Slideshow Background */}
        <section className="psdct-section py-16 px-6 mt-16 relative overflow-hidden">
          {/* Slideshow Background */}
          <div className="slideshow-container">
            {slides.map((slide, index) => (
              <div key={index} className={`slide ${index === currentSlide ? "active" : ""}`}>
                <img src={slide || "/placeholder.svg"} alt={`Construction Site ${index + 1}`} />
              </div>
            ))}

            {/* Overlay gradients */}
            <div className="slideshow-overlay"></div>
            <div className="slideshow-overlay-2"></div>
          </div>

          {/* Animated background elements */}
          <div className="bg-element-1"></div>
          <div className="bg-element-2"></div>
          <div className="bg-element-3"></div>

          <div className="max-w-5xl mx-auto relative z-10">
            <div className="bg-white/80 backdrop-blur-xl shadow-2xl border border-white/20 rounded-2xl feature-card p-8">
                <h1 className="text-3xl md:text-4xl font-bold mb-6 text-center fade-in-element fade-in-delay-1 relative">
                <span className="title-gradient">PSDCT – Professional Structural Design Certification Training</span>
                <div className="title-glow"></div>
                </h1>
                <p className="text-lg text-center mb-8 text-gray-600 leading-relaxed content-animate fade-in-delay-2">
                PSDCT Live Mode is our flagship real-time online training program designed to help aspiring structural
                engineers become industry-ready with hands-on skills.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                <div className="space-y-4 content-animate fade-in-delay-3">
                    <h2 className="text-xl font-semibold text-[#290101] mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                        />
                    </svg>
                    Course Highlights
                    </h2>
                    <ul className="space-y-3">
                    {[
                        "Live Zoom classes with experienced structural engineers",
                        "Hands-on training in ETABS, STAAD.Pro, SAFE, AutoCAD, Tekla",
                        "Manual RCC & Steel design practices",
                        "Real-world design case studies & assignments",
                        "Interview prep and career guidance",
                    ].map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-700">
                        <div className="w-2 h-2 bg-gradient-to-r from-[#06b6d4] to-[#7c3aed] rounded-full mt-2 flex-shrink-0"></div>
                        <span>{item}</span>
                        </li>
                    ))}
                    </ul>
                </div>

                <div className="space-y-4 content-animate fade-in-delay-4">
                    <h2 className="text-xl font-semibold text-[#290101] mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                        />
                    </svg>
                    Schedule & Access
                    </h2>
                    <ul className="space-y-3">
                    {[
                        "New batches start every 1st and 15th of the month",
                        "Sessions held on evenings and weekends (flexible)",
                        "All sessions recorded for later access",
                        "WhatsApp/Email support for doubts and follow-ups",
                        "Certificate issued upon completion and evaluation",
                    ].map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-700">
                        <div className="w-2 h-2 bg-gradient-to-r from-[#06b6d4] to-[#7c3aed] rounded-full mt-2 flex-shrink-0"></div>
                        <span>{item}</span>
                        </li>
                    ))}
                    </ul>
                </div>
                </div>

                <div className="text-center fade-in-element fade-in-delay-4">
                <Link
                    to="/register"
                    className="relative inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-white transition-transform duration-300 rounded-full bg-gradient-to-r from-[#06b6d4] to-[#7c3aed] hover:from-[#0f766e] hover:to-[#5b21b6] hover:scale-105 shadow-lg hover:shadow-[0_0_20px_#06b6d4]"
                    >
                    <span className="relative z-10 flex items-center gap-2">
                        Enroll in PSDCT Live Mode
                        <svg
                        className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        >
                        <path
                            fillRule="evenodd"
                            d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                        </svg>
                    </span>

                    {/* Glow Effect */}
                    <span className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/50 to-[#7c3aed]/50 opacity-50 blur-lg rounded-full animate-pulse"></span>
                    </Link>

                </div>
            </div>
            </div>
        </section>

        {/* Footer - Same as About Page */}
        <footer className="bg-gray-900 text-white relative overflow-hidden">
          <div className="relative z-10 py-12">
            <div className="container mx-auto px-6">
              {/* Main Footer Navigation */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <Link to="/" className="footer-link">
                  <span>Home</span>
                </Link>
                <Link to="/psdct" className="footer-link">
                  <span>Psdct Live Mode</span>
                </Link>
                <Link to="/terms" className="footer-link">
                  <span>Term & Conditions</span>
                </Link>
                <Link to="/quick-payment" className="footer-link">
                  <span>Quick Pay Any Fees</span>
                </Link>
                <Link to="/registration-policy" className="footer-link">
                  <span>Registration Policy</span>
                </Link>
                <Link to="/cancel" className="footer-link">
                  <span>Cancellation Policy</span>
                </Link>
                <Link to="/careers" className="footer-link">
                  <span>Careers</span>
                </Link>
                <Link to="/contact" className="footer-link">
                  <span>Contact Us</span>
                </Link>
                <Link to="/franchise" className="footer-link">
                  <span>Franchise</span>
                </Link>
                <Link to="/free" className="footer-link">
                  <span>Free</span>
                </Link>
                <Link to="/students" className="footer-link">
                  <span>Students</span>
                </Link>
                <Link to="/training" className="footer-link">
                  <span>Training Centers</span>
                </Link>
              </div>

              {/* Bottom Section */}
              <div className="border-t border-gray-700 pt-8">
                <div className="text-center fade-in-element">
                  <p className="text-sm text-gray-400">
                    &copy; 2025 StructuralGuru - GURURAYAR ASSOCIATES. All Rights Reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
